import { z } from "zod";

import { taskMetadataBaseSchema } from "./taskMetadata";

const nonEmptyStringSchema = z.string().trim().min(1);
const nullableNonEmptyStringSchema = z.string().trim().min(1).nullable();

export const ISSUE_COMMENT_INCOMPATIBLE_RESPONSE_SCHEMA_NAME =
  "issue.comment.incompatible.v1" as const;

export const ISSUE_COMMENT_INCOMPATIBLE_FLOW_KIND =
  "comment_reply.incompatible.v1" as const;

export const issueCommentIncompatibleReplySchema = z
  .object({
    reply: z
      .string()
      .trim()
      .min(1, "reply must include a friendly message")
      .max(800, "reply should remain concise"),
  })
  .strict();

export type IssueCommentIncompatibleReply = z.infer<
  typeof issueCommentIncompatibleReplySchema
>;

export const issueCommentIncompatibleTaskMetadataSchema = taskMetadataBaseSchema
  .extend({
    classification: nonEmptyStringSchema,
    expected_response_schema_name: z.literal(
      ISSUE_COMMENT_INCOMPATIBLE_RESPONSE_SCHEMA_NAME,
    ),
    flow: z
      .object({
        comment_id: nonEmptyStringSchema,
        issue_id: nullableNonEmptyStringSchema,
        issue_key: nonEmptyStringSchema,
        kind: z.literal(ISSUE_COMMENT_INCOMPATIBLE_FLOW_KIND),
        project_id: nullableNonEmptyStringSchema,
        project_key: nullableNonEmptyStringSchema,
        provider_id: nonEmptyStringSchema,
        provider_type: nonEmptyStringSchema,
      })
      .strict(),
    issue_id: nullableNonEmptyStringSchema,
    issue_key: nonEmptyStringSchema,
    project_id: nullableNonEmptyStringSchema,
    project_key: nullableNonEmptyStringSchema,
    provider_id: nonEmptyStringSchema,
    provider_type: nonEmptyStringSchema,
    source: z.literal("issue_comment_flow"),
  })
  .strict();

export type IssueCommentIncompatibleTaskMetadata = z.infer<
  typeof issueCommentIncompatibleTaskMetadataSchema
>;

export const issueCommentIncompatibleTaskPayloadSchema = z
  .object({
    context: z
      .object({
        comment: z
          .object({
            author: z.string().trim().min(1).nullable(),
            body: z.string(),
            id: nonEmptyStringSchema,
          })
          .strict(),
        issue: z
          .object({
            classification: nonEmptyStringSchema,
            key: nonEmptyStringSchema,
            summary: z.string().nullable(),
          })
          .strict(),
      })
      .strict(),
    expected_response_schema_name: z.literal(
      ISSUE_COMMENT_INCOMPATIBLE_RESPONSE_SCHEMA_NAME,
    ),
    mode: z.literal("execute"),
    prompt: z.string().min(1),
    provider: z.literal("arti"),
  })
  .strict();

export type IssueCommentIncompatibleTaskPayload = z.infer<
  typeof issueCommentIncompatibleTaskPayloadSchema
>;

export function parseIssueCommentIncompatibleTaskMetadata(
  value: unknown,
): IssueCommentIncompatibleTaskMetadata | null {
  const parsed = issueCommentIncompatibleTaskMetadataSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
